import React, { useState, useEffect } from 'react';
import api from '../../api';
import AdminLayout from '../../components/AdminLayout';

export default function AdminSubscribers() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // 'all' | 'active' | 'unsubscribed'

  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/auth/subscribers')
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.error || err.message || 'Failed to load subscribers'));
  }, []);

  const [sendingNewsletter, setSendingNewsletter] = useState(false);

  const sendNewsletter = async () => {
    if (!window.confirm(`Send weekly newsletter to ${data?.active || 0} active subscribers?`)) return;
    setSendingNewsletter(true);
    try {
      const r = await api.post('/auth/send-newsletter');
      alert(`✅ Newsletter sent!\nDelivered: ${r.data.sent}\nFailed: ${r.data.failed}\nTotal: ${r.data.total}`);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    } finally { setSendingNewsletter(false); }
  };

  const exportCSV = () => {
    if (!data) return;
    const rows = [['Email', 'Source', 'Status', 'Joined']];
    data.subscribers.forEach(s => rows.push([
      s.email, s.source || 'popup',
      s.unsubscribed ? 'Unsubscribed' : 'Active',
      new Date(s.created_at).toLocaleDateString()
    ]));
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'styleme-subscribers.csv'; a.click();
  };

  const filtered = (data?.subscribers || []).filter(s => {
    const matchSearch = !search || s.email.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || (filter === 'active' && !s.unsubscribed) || (filter === 'unsubscribed' && s.unsubscribed);
    return matchSearch && matchFilter;
  });

  return (
    <AdminLayout title="Email Subscribers">
      {error && <div style={{ background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:8, padding:'12px 16px', marginBottom:20, color:'#ef4444', fontWeight:600 }}>⚠️ {error}</div>}
      {!data && !error && <div style={{ textAlign:'center', padding:40, color:'#9ca3af' }}>Loading...</div>}
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total', value: data?.total ?? '–', color: '#c9a227', icon: '📧' },
          { label: 'Active', value: data?.active ?? '–', color: '#10b981', icon: '✅' },
          { label: 'Unsubscribed', value: data?.unsubscribed ?? '–', color: '#ef4444', icon: '🚫' },
        ].map(s => (
          <div key={s.label} style={{ background: 'white', borderRadius: 12, padding: '20px 20px', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 28 }}>{s.icon}</span>
            <div>
              <div style={{ fontSize: 26, fontWeight: 900, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="Search email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '9px 14px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, flex: 1, minWidth: 200 }}
        />
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'active', 'unsubscribed'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                borderColor: filter === f ? '#c9a227' : '#e5e7eb', background: filter === f ? '#c9a227' : 'white', color: filter === f ? 'white' : '#374151', textTransform: 'capitalize' }}>
              {f}
            </button>
          ))}
        </div>
        <button onClick={exportCSV} className="btn btn-outline btn-sm">
          ⬇️ Export CSV
        </button>
        <button onClick={sendNewsletter} disabled={sendingNewsletter}
          style={{ padding:'8px 16px', background:'linear-gradient(135deg,#c9a227,#9a7a0c)', color:'white', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>
          {sendingNewsletter ? '📨 Sending...' : '📨 Send Newsletter Now'}
        </button>
      </div>

      {/* Table */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: '#f9fafb' }}>
            <tr>
              {['Email', 'Source', 'Status', 'Joined'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={4} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>No subscribers found</td></tr>
            ) : filtered.map(s => (
              <tr key={s.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 500 }}>{s.email}</td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280', textTransform: 'capitalize' }}>{s.source || 'popup'}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 100,
                    background: s.unsubscribed ? '#fee2e2' : '#d1fae5', color: s.unsubscribed ? '#ef4444' : '#10b981' }}>
                    {s.unsubscribed ? 'Unsubscribed' : 'Active'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', fontSize: 13, color: '#6b7280' }}>
                  {new Date(s.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 13, color: '#9ca3af', marginTop: 12 }}>{filtered.length} of {data?.total || 0} subscribers shown</p>
    </AdminLayout>
  );
}
